import type mapboxgl from 'mapbox-gl';

type AtlasGlobalWindow = Window & {
  __psxGlyphGzipWorkaroundInstalled?: boolean;
  DecompressionStream?: new (format: string) => TransformStream;
};

export const installAtlasGlyphGzipWorkaround = (): void => {
  const globalScope = window as AtlasGlobalWindow;

  if (globalScope.__psxGlyphGzipWorkaroundInstalled) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.toString()
        : input.url;
    const isGlyphPbf =
      url.includes('/data/fonts/') &&
      (url.endsWith('.pbf') || url.includes('.pbf?'));

    if (!isGlyphPbf) {
      return originalFetch(input, init);
    }

    const response = await originalFetch(input, init);
    if (!response.ok) {
      return response;
    }

    const existingEncoding = response.headers.get('content-encoding') || '';
    if (existingEncoding.toLowerCase().includes('gzip')) {
      return response;
    }

    const compressedBuffer = await response.arrayBuffer();
    const compressedBytes = new Uint8Array(compressedBuffer);
    const looksGzipped =
      compressedBytes.length > 2 &&
      compressedBytes[0] === 0x1f &&
      compressedBytes[1] === 0x8b;

    if (!looksGzipped || !globalScope.DecompressionStream) {
      return new Response(compressedBuffer, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    }

    try {
      const decompressionStream = new Blob([compressedBuffer])
        .stream()
        .pipeThrough(new globalScope.DecompressionStream('gzip'));
      const decompressedBuffer = await new Response(
        decompressionStream
      ).arrayBuffer();

      const headers = new Headers(response.headers);
      headers.delete('content-encoding');
      headers.set('content-type', 'application/x-protobuf');
      headers.set('content-length', String(decompressedBuffer.byteLength));

      return new Response(decompressedBuffer, {
        status: response.status,
        statusText: response.statusText,
        headers,
      });
    } catch {
      return new Response(compressedBuffer, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    }
  };

  globalScope.__psxGlyphGzipWorkaroundInstalled = true;
};

export const applyAtlasStyleWorkarounds = (
  style: Record<string, any>,
  atlasHostUrl: string,
  pmtilesUrl: string
): void => {
  style.sprite = `${atlasHostUrl}/data/sprites/streets-v12`;
  style.glyphs = `${atlasHostUrl}/data/fonts/mapbox/{fontstack}/{range}.pbf`;

  if (style.sources?.composite) {
    style.sources.composite = {
      type: 'vector',
      url: pmtilesUrl,
    };
  }

  if (style.sources) {
    Object.values(style.sources).forEach((source: any) => {
      if (
        source?.url &&
        typeof source.url === 'string' &&
        source.url.startsWith('mapbox://')
      ) {
        source.url = pmtilesUrl;
      }
    });
  }

  if (Array.isArray(style.layers)) {
    style.layers.forEach((layer: any) => {
      if (layer?.type !== 'symbol') {
        return;
      }

      layer.layout = layer.layout || {};
      layer.layout['text-font'] = ['Arial Unicode MS Regular'];

      if (layer['source-layer'] === 'road' && layer.layout['text-field']) {
        layer.minzoom = Math.min(layer.minzoom ?? 24, 8);
      }
    });
  }
};

export const installAtlasLabelFallbackOnIdle = (map: mapboxgl.Map): void => {
  let labelsPatched = false;

  map.on('idle', () => {
    if (labelsPatched) {
      return;
    }

    try {
      const sourceLayerCandidates: Record<string, string[]> = {
        road: ['name', 'name_en', 'name:en', 'name_int', 'name_local', 'ref'],
        place_label: ['name_en', 'name', 'name:en', 'name_int', 'name_local'],
        natural_label: ['name_en', 'name', 'name:en', 'name_int', 'name_local'],
        poi_label: ['name_en', 'name', 'name:en', 'name_int', 'name_local'],
      };

      const allLayers = map.getStyle().layers || [];

      Object.entries(sourceLayerCandidates).forEach(
        ([sourceLayer, candidateKeys]) => {
          const features = map
            .querySourceFeatures('composite', { sourceLayer })
            .slice(0, 300);

          if (!features.length) {
            return;
          }

          let bestKey = '';

          for (const key of candidateKeys) {
            if (
              features.some(
                (feature: any) =>
                  typeof feature.properties?.[key] === 'string' &&
                  feature.properties[key].trim()
              )
            ) {
              bestKey = key;
              break;
            }
          }

          if (!bestKey) {
            const keys = Object.keys(features[0]?.properties || {});
            bestKey =
              keys.find((key) => key.toLowerCase().includes('name')) || '';
          }

          if (!bestKey) {
            return;
          }

          const targetLayers = allLayers.filter(
            (layer: any) =>
              layer.type === 'symbol' &&
              layer['source-layer'] === sourceLayer &&
              layer.layout?.['text-field']
          );

          targetLayers.forEach((layer: any) => {
            const fallbackExpression =
              sourceLayer === 'road'
                ? [
                    'coalesce',
                    ['get', bestKey],
                    ['get', 'name'],
                    ['get', 'ref'],
                  ]
                : ['coalesce', ['get', bestKey], ['get', 'name']];

            map.setLayoutProperty(
              layer.id,
              'text-field',
              fallbackExpression as any
            );
          });
        }
      );

      labelsPatched = true;
    } catch {}
  });
};

// TODO: Remove these client-side workarounds after Atlas serves glyph .pbf with Content-Encoding: gzip.
